import { create } from 'zustand';
import { createTranslator, type Dict, type Lang, type Translator } from './core';
import { en } from './en';
import { protoEn } from './proto.generated';
import { imageEn } from './image';
import { umEn } from './um';
import { cfgEn } from './console';
import { emailEn } from './email';
import { rolesEn } from './roles';
import { accountEn } from './account';
import { socialEn } from './social';
import { hubTextEn } from './hubText';
import { settingsEn } from './settings';
import { pokesEn } from './pokes';
import { pollsEn } from './polls';
import { notifyEn } from './notify';
import { automodEn } from './automod';
import { shellEn } from './overhaul/shell';
import { chatEn } from './overhaul/chat';
import { kitEn } from './overhaul/kit';
import { voiceEn } from './overhaul/voice';
import { settingsEn as overhaulSettingsEn } from './overhaul/settings';
import { peopleEn } from './overhaul/people';
import { streamEn } from './stream';
import { threadsEn } from './threads';
import { emojiEn } from './emoji';
import { eventsEn } from './events';
import { nextSocialEn } from './nextSocial';
import { chatUiEn } from './chatUi';
import { callsEn } from './calls';
import { voiceExtrasEn } from './voiceExtras';
// Console v2 (docs/design/console-v2.md §5): each slice's strings
import { governanceV2En } from './v2/governance';
import { moderationV2En } from './v2/moderation';
import { releasesV2En } from './v2/releases';
import { serversV2En } from './v2/servers';

export type { Lang, Translator } from './core';
export { plainText } from './core';

/** The app's interface is English only (owner decision 2026-10-29). */
export const dictionaries: Record<Lang, Dict> = {
  en: {
    ...protoEn,
    ...en,
    ...umEn,
    ...cfgEn,
    ...imageEn,
    ...emailEn,
    ...rolesEn,
    ...accountEn,
    ...socialEn,
    ...hubTextEn,
    ...settingsEn,
    ...pokesEn,
    ...pollsEn,
    ...notifyEn,
    ...automodEn,
    // the visual overhaul (WP1-WP6) may override any key above
    ...shellEn,
    ...chatEn,
    ...kitEn,
    ...voiceEn,
    ...overhaulSettingsEn,
    ...peopleEn,
    ...streamEn,
    ...threadsEn,
    // custom server emoji (2026-11-01)
    ...emojiEn,
    // scheduled events (2026-10-29)
    ...eventsEn,
    // the social feature (2026-10-29): new keys only
    ...nextSocialEn,
    ...chatUiEn,
    // DM and group DM calls (2026-11-01)
    ...callsEn,
    // the voice extras (2026-11-01); overrides ns.voice.processing
    ...voiceExtrasEn,
    // Console v2 (2026-11-02): new keys only
    ...serversV2En,
    ...moderationV2En,
    ...releasesV2En,
    ...governanceV2En,
  },
};

interface I18nState {
  lang: Lang;
  tr: Translator;
}

/**
 * Puts the language on the root. The interface is English and left to
 * right; what people write keeps its own direction (`dir="auto"`, `<bdi>`).
 */
export function applyDocumentLang(): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = 'en';
  document.documentElement.dir = 'ltr';
}

export const useI18n = create<I18nState>(() => ({
  lang: 'en',
  tr: createTranslator('en', dictionaries),
}));

/** The translator; a stable object for the life of the app. */
export function useT(): Translator {
  return useI18n((s) => s.tr);
}

/** The translator outside React. */
export function tr(): Translator {
  return useI18n.getState().tr;
}
